import React, { useEffect, useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { AirVentIcon, ThermometerIcon, WrenchIcon, CheckCircleIcon, TrophyIcon, ClockIcon, UsersIcon, BadgeCheckIcon, GlobeIcon, BuildingIcon, FactoryIcon, ChevronRightIcon, ChevronDownIcon, PlayCircleIcon } from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};
const StatCounter = ({
  end,
  suffix,
  title,
  description
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.3
  });
  return <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeIn} className="bg-white rounded-lg shadow-lg p-6 text-center transform hover:scale-105 transition-transform duration-300" data-id="element-427">
      <h3 className="text-4xl font-bold text-red-600 mb-2" data-id="element-428">
        {inView ? <CountUp end={end} duration={2.5} suffix={suffix} data-id="element-429" /> : '0'}
      </h3>
      <p className="text-xl font-semibold mb-2" data-id="element-430">{title}</p>
      <p className="text-gray-600" data-id="element-431">{description}</p>
    </motion.div>;
};
// YouTube video component
const VideoEmbed = ({
  embedId,
  title
}) => {
  return <div className="aspect-video rounded-lg overflow-hidden shadow-lg" data-id="element-432">
      <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${embedId}`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-433"></iframe>
    </div>;
};
const HomePage = () => {
  const [scrollY, setScrollY] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  // Handle parallax effect
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Technical expertise data
  const expertise = [{
    title: 'Fire Safety Engineering',
    percentage: 95,
    color: 'bg-red-600'
  }, {
    title: 'Mechanical Systems',
    percentage: 90,
    color: 'bg-blue-600'
  }, {
    title: 'HVAC Solutions',
    percentage: 85,
    color: 'bg-green-600'
  }, {
    title: 'Pipeline Engineering',
    percentage: 92,
    color: 'bg-yellow-600'
  }];
  // Certifications data
  const certifications = [{
    name: 'ISO 9001:2015',
    description: 'Quality Management Systems',
    icon: <BadgeCheckIcon size={32} className="text-red-600" data-id="element-434" />
  }, {
    name: 'ISO 14001:2015',
    description: 'Environmental Management',
    icon: <GlobeIcon size={32} className="text-red-600" data-id="element-435" />
  }, {
    name: 'OHSAS 18001',
    description: 'Occupational Health & Safety',
    icon: <BuildingIcon size={32} className="text-red-600" data-id="element-436" />
  }, {
    name: 'API Standards',
    description: 'American Petroleum Institute',
    icon: <FactoryIcon size={32} className="text-red-600" data-id="element-437" />
  }];
  // Video showcase data
  const videos = [{
    id: 'Ym_EpwXZOJA',
    title: 'Fire Protection Systems Installation'
  }, {
    id: 'LXb3EKWsInQ',
    title: 'Pipeline Engineering Projects'
  }, {
    id: 'eXpIc0IGqus',
    title: 'HVAC Systems Maintenance'
  }];
  return <div className="w-full" data-id="element-438">
      {/* Hero Section with Parallax */}
      <section className="relative bg-gray-900 text-white overflow-hidden" data-id="element-439">
        <div className="absolute inset-0 bg-black opacity-60 z-10" data-id="element-440"></div>
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: "url('https://uploadthingy.s3.us-west-1.amazonaws.com/gj59xh5o8FP3M5iDrq1yB2/IMG-20250828-WA0023.jpg')",
        transform: `translateY(${scrollY * 0.3}px)`,
        backgroundBlendMode: 'overlay'
      }} data-id="element-441"></div>
        <div className="container mx-auto px-4 py-32 md:py-40 relative z-20" data-id="element-442">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-3xl" data-id="element-443">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight" data-id="element-444">
              Engineered for <span className="text-red-600" data-id="element-445">Excellence</span>
              <span className="block mt-2" data-id="element-446">Built for the Future</span>
            </h1>
            <p className="text-xl mb-8 leading-relaxed" data-id="element-447">
              Daplamba Investments Limited delivers cutting-edge engineering
              solutions for complex technical challenges across Uganda and East
              Africa.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-448">
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-449">
                <Link to="/contact" className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-md font-medium transition-colors text-center flex items-center justify-center" data-id="element-450">
                  Request a Quote{' '}
                  <ChevronRightIcon size={18} className="ml-2" data-id="element-451" />
                </Link>
              </motion.div>
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-452">
                <Link to="/services" className="bg-transparent border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-4 rounded-md font-medium transition-colors text-center flex items-center justify-center" data-id="element-453">
                  Explore Services{' '}
                  <ChevronRightIcon size={18} className="ml-2" data-id="element-454" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
        {/* Scroll indicator */}
        <motion.div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20" animate={{
        y: [0, 10, 0]
      }} transition={{
        repeat: Infinity,
        duration: 1.5
      }} data-id="element-455">
          <ChevronDownIcon size={32} className="text-white opacity-80" data-id="element-456" />
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-457">
        <div className="container mx-auto px-4" data-id="element-458">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-459">
            <StatCounter end={25} suffix="+" title="Years Experience" description="Delivering excellence in engineering" data-id="element-460" />
            <StatCounter end={500} suffix="+" title="Projects Completed" description="Across East Africa and beyond" data-id="element-461" />
            <StatCounter end={100} suffix="%" title="Client Satisfaction" description="Our commitment to quality" data-id="element-462" />
            <StatCounter end={45} suffix="" title="Expert Engineers" description="Highly qualified professionals" data-id="element-463" />
          </motion.div>
        </div>
      </section>

      {/* Introduction Section with Animation */}
      <section className="py-20 bg-white" data-id="element-464">
        <div className="container mx-auto px-4" data-id="element-465">
          <div className="flex flex-col md:flex-row items-center" data-id="element-466">
            <motion.div className="md:w-1/2 mb-10 md:mb-0 md:pr-8" initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-467">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/q9Rzyr8MSfeD8mb3Xz6E1b/IMG-20250828-WA0026.jpg" alt="Daplamba Engineering Work" className="rounded-lg shadow-2xl" data-id="element-468" />
            </motion.div>
            <motion.div className="md:w-1/2" initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} data-id="element-469">
              <h2 className="text-3xl font-bold mb-6" data-id="element-470">
                <span className="border-b-4 border-red-600 pb-2" data-id="element-471">About</span>{' '}
                Daplamba Investments Limited
              </h2>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed" data-id="element-472">
                Daplamba Investments Limited is a premier technical mechanical
                works company in Uganda, specializing in the installation,
                repair, and maintenance of complex engineering systems. With
                decades of experience and a team of qualified engineers, we
                deliver innovative, safe, and reliable engineering solutions
                across East Africa.
              </p>
              <p className="text-gray-700 mb-8 text-lg leading-relaxed" data-id="element-473">
                Our expertise spans fire protection systems, underground leakage
                control, compressed air systems, HVAC solutions, diesel pump
                engines, plumbing installations, and oil & gas mechanical
                services. We serve both domestic and industrial clients with the
                highest standards of quality and safety.
              </p>
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-474">
                <Link to="/about" className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-md font-medium transition-colors inline-flex items-center" data-id="element-475">
                  Learn More About Us{' '}
                  <ChevronRightIcon size={18} className="ml-2" data-id="element-476" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Technical Expertise Section */}
      <section className="py-20 bg-gray-50" data-id="element-477">
        <div className="container mx-auto px-4" data-id="element-478">
          <motion.div className="text-center mb-12" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-479">
            <h2 className="text-3xl font-bold mb-4" data-id="element-480">
              <span className="border-b-4 border-red-600 pb-2" data-id="element-481">Technical</span>{' '}
              Expertise
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg" data-id="element-482">
              Our engineering capabilities are backed by years of experience and
              technical knowledge in various specialized fields.
            </p>
          </motion.div>
          <div className="max-w-3xl mx-auto" data-id="element-483">
            {expertise.map((skill, index) => <motion.div key={index} className="mb-8" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} data-id="element-484">
                <div className="flex justify-between mb-2" data-id="element-485">
                  <span className="font-semibold" data-id="element-486">{skill.title}</span>
                  <span className="font-semibold" data-id="element-487">{skill.percentage}%</span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden" data-id="element-488">
                  <motion.div className={`h-full ${skill.color}`} initial={{
                width: 0
              }} whileInView={{
                width: `${skill.percentage}%`
              }} viewport={{
                once: true
              }} transition={{
                duration: 1.5,
                delay: 0.2
              }} data-id="element-489"></motion.div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Featured Project Section */}
      <section className="py-16 bg-white" data-id="element-490">
        <div className="container mx-auto px-4" data-id="element-491">
          <motion.div className="text-center mb-12" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-492">
            <h2 className="text-3xl font-bold mb-4" data-id="element-493">
              <span className="border-b-4 border-red-600 pb-2" data-id="element-494">Featured</span>{' '}
              Projects
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg" data-id="element-495">
              See our engineering expertise in action through our recent project
              work across Uganda
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-496">
            <motion.div className="rounded-lg overflow-hidden shadow-lg" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} whileHover={{
            y: -10
          }} data-id="element-497">
              <div className="h-64 overflow-hidden" data-id="element-498">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dFbZ7TfA7BeXxibRpzWd7c/IMG-20250828-WA0022.jpg" alt="Pipeline Installation Project" className="w-full h-full object-cover" data-id="element-499" />
              </div>
              <div className="p-6" data-id="element-500">
                <span className="text-red-600 font-medium text-sm" data-id="element-501">
                  Pipeline Systems
                </span>
                <h3 className="text-xl font-bold mt-2 mb-3" data-id="element-502">
                  Underground Pipeline Installation
                </h3>
                <p className="text-gray-600" data-id="element-503">
                  Precision installation of high-pressure pipeline systems with
                  advanced joining technology.
                </p>
              </div>
            </motion.div>
            <motion.div className="rounded-lg overflow-hidden shadow-lg" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} whileHover={{
            y: -10
          }} data-id="element-504">
              <div className="h-64 overflow-hidden" data-id="element-505">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/sxZnRZGbG5p2zjUJbrwqXT/IMG-20250828-WA0024.jpg" alt="Industrial Pipe Installation" className="w-full h-full object-cover" data-id="element-506" />
              </div>
              <div className="p-6" data-id="element-507">
                <span className="text-red-600 font-medium text-sm" data-id="element-508">
                  Industrial Systems
                </span>
                <h3 className="text-xl font-bold mt-2 mb-3" data-id="element-509">
                  Large-Scale Pipe Transport
                </h3>
                <p className="text-gray-600" data-id="element-510">
                  Handling and installation of industrial-grade piping systems
                  for major facilities.
                </p>
              </div>
            </motion.div>
            <motion.div className="rounded-lg overflow-hidden shadow-lg" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} whileHover={{
            y: -10
          }} data-id="element-511">
              <div className="h-64 overflow-hidden" data-id="element-512">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3hTgHUcfHSQHoQfP4YQjDw/IMG-20250828-WA0025.jpg" alt="Pump System Installation" className="w-full h-full object-cover" data-id="element-513" />
              </div>
              <div className="p-6" data-id="element-514">
                <span className="text-red-600 font-medium text-sm" data-id="element-515">
                  Mechanical Systems
                </span>
                <h3 className="text-xl font-bold mt-2 mb-3" data-id="element-516">
                  Pump & Motor Systems
                </h3>
                <p className="text-gray-600" data-id="element-517">
                  Installation and maintenance of specialized pumping equipment
                  for water and industrial applications.
                </p>
              </div>
            </motion.div>
          </div>
          <div className="text-center mt-12" data-id="element-518">
            <motion.div whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-519">
              <Link to="/projects" className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-medium transition-colors inline-flex items-center" data-id="element-520">
                View All Projects{' '}
                <ChevronRightIcon size={18} className="ml-2" data-id="element-521" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-20 bg-gray-900 text-white" data-id="element-522">
        <div className="container mx-auto px-4" data-id="element-523">
          <motion.div className="text-center mb-12" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-524">
            <h2 className="text-3xl font-bold mb-4" data-id="element-525">
              <span className="border-b-4 border-red-600 pb-2" data-id="element-526">Video</span>{' '}
              Showcase
            </h2>
            <p className="text-gray-300 max-w-3xl mx-auto text-lg mb-8" data-id="element-527">
              Watch our engineering projects in action through these video
              demonstrations
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-528">
            {videos.map((video, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.2
          }} className="flex flex-col" data-id="element-529">
                <VideoEmbed embedId={video.id} title={video.title} data-id="element-530" />
                <div className="mt-4 text-center" data-id="element-531">
                  <h3 className="text-xl font-semibold flex items-center justify-center" data-id="element-532">
                    <PlayCircleIcon className="mr-2 text-red-500" size={20} data-id="element-533" />
                    {video.title}
                  </h3>
                </div>
              </motion.div>)}
          </div>
          <motion.div className="text-center mt-12" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-534">
            <a href="https://www.youtube.com/channel/REPLACE_WITH_YOUR_CHANNEL_ID" target="_blank" rel="noopener noreferrer" className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-medium transition-colors inline-flex items-center" data-id="element-535">
              Visit Our YouTube Channel{' '}
              <ChevronRightIcon size={18} className="ml-2" data-id="element-536" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Services Section with Improved Cards */}
      <section className="py-20 bg-gray-50" data-id="element-537">
        <div className="container mx-auto px-4" data-id="element-538">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-539">
            <h2 className="text-3xl font-bold mb-4" data-id="element-540">
              <span className="border-b-4 border-red-600 pb-2" data-id="element-541">
                Engineering
              </span>{' '}
              Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-542">
              We provide a comprehensive range of engineering and mechanical
              services to meet the diverse needs of our clients across Uganda
              and East Africa.
            </p>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} data-id="element-543">
            <ServiceCard title="Fire Protection Systems" description="Supply, installation, and maintenance of fire-fighting pumps, sprinkler systems, hydrants, and fire suppression equipment." icon={<div size={24} data-id="element-545" />} data-id="element-544" />
            <ServiceCard title="Underground Leakage Control" description="Detection, repair, and replacement of underground water and gas pipelines, ensuring efficiency and safety." icon={<div size={24} data-id="element-547" />} data-id="element-546" />
            <ServiceCard title="Compressed Air Systems" description="Design, assembly, and installation of compressor systems for industrial and commercial use, including servicing and maintenance." icon={<AirVentIcon size={24} data-id="element-549" />} data-id="element-548" />
            <ServiceCard title="HVAC & Chiller Systems" description="Installation and servicing of chiller towers, air conditioning systems, and industrial cooling systems." icon={<ThermometerIcon size={24} data-id="element-551" />} data-id="element-550" />
            <ServiceCard title="Diesel Pump Engines" description="Installation, repair, and preventive maintenance of diesel pump engines used in water systems, fire systems, and industrial processes." icon={<div size={24} data-id="element-553" />} data-id="element-552" />
            <ServiceCard title="Oil & Gas Mechanical Services" description="Specialized services in installation, inspection, and maintenance of oil and gas handling systems." icon={<div size={24} data-id="element-555" />} data-id="element-554" />
          </motion.div>
          <motion.div className="text-center mt-12" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-556">
            <Link to="/services" className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-medium transition-colors inline-flex items-center" data-id="element-557">
              View All Services <ChevronRightIcon size={18} className="ml-2" data-id="element-558" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-20 bg-gray-900 text-white" data-id="element-559">
        <div className="container mx-auto px-4" data-id="element-560">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-561">
            <h2 className="text-3xl font-bold mb-4" data-id="element-562">
              <span className="border-b-4 border-red-600 pb-2" data-id="element-563">Industry</span>{' '}
              Certifications
            </h2>
            <p className="text-gray-300 max-w-3xl mx-auto text-lg" data-id="element-564">
              Our work adheres to international standards and best practices,
              backed by recognized industry certifications.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-565">
            {certifications.map((cert, index) => <motion.div key={index} className="bg-gray-800 p-6 rounded-lg text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} whileHover={{
            y: -10
          }} data-id="element-566">
                <div className="mb-4 flex justify-center" data-id="element-567">{cert.icon}</div>
                <h3 className="text-xl font-bold mb-2" data-id="element-568">{cert.name}</h3>
                <p className="text-gray-400" data-id="element-569">{cert.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white" data-id="element-570">
        <div className="container mx-auto px-4" data-id="element-571">
          <motion.div className="text-center mb-16" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-572">
            <h2 className="text-3xl font-bold mb-4" data-id="element-573">
              <span className="border-b-4 border-red-600 pb-2" data-id="element-574">Why</span> Choose
              Us
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto text-lg" data-id="element-575">
              At Daplamba Investments Limited, we pride ourselves on delivering
              exceptional engineering solutions that meet and exceed our
              clients' expectations.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-576">
            <motion.div className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} whileHover={{
            y: -10
          }} data-id="element-577">
              <div className="bg-red-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" data-id="element-578">
                <CheckCircleIcon size={40} data-id="element-579" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-580">Quality Assurance</h3>
              <p className="text-gray-300" data-id="element-581">
                We adhere to international standards and best practices in all
                our engineering projects, ensuring the highest quality in every
                detail.
              </p>
            </motion.div>
            <motion.div className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} whileHover={{
            y: -10
          }} data-id="element-582">
              <div className="bg-red-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" data-id="element-583">
                <UsersIcon size={40} data-id="element-584" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-585">Expert Team</h3>
              <p className="text-gray-300" data-id="element-586">
                Our engineers and technicians are highly qualified with
                extensive industry experience and continuous professional
                development.
              </p>
            </motion.div>
            <motion.div className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} whileHover={{
            y: -10
          }} data-id="element-587">
              <div className="bg-red-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" data-id="element-588">
                <ClockIcon size={40} data-id="element-589" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-590">Timely Delivery</h3>
              <p className="text-gray-300" data-id="element-591">
                We complete all projects on schedule without compromising on
                quality or safety, respecting your time and project deadlines.
              </p>
            </motion.div>
            <motion.div className="text-center" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} whileHover={{
            y: -10
          }} data-id="element-592">
              <div className="bg-red-600 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6" data-id="element-593">
                <TrophyIcon size={40} data-id="element-594" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-595">Proven Track Record</h3>
              <p className="text-gray-300" data-id="element-596">
                Our portfolio includes successful projects for leading
                organizations across East Africa, demonstrating our capability
                and reliability.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section with Gradient */}
      <section className="py-20 bg-gradient-to-r from-red-700 to-red-500 text-white" data-id="element-597">
        <div className="container mx-auto px-4 text-center" data-id="element-598">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-599">
            <h2 className="text-4xl font-bold mb-6" data-id="element-600">
              Ready to Engineer Your Success?
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto" data-id="element-601">
              Contact us today to discuss your engineering needs and discover
              how Daplamba Investments Limited can deliver technical excellence
              for your next project.
            </p>
            <motion.div whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} data-id="element-602">
              <Link to="/contact" className="bg-white text-red-600 hover:bg-gray-100 px-10 py-4 rounded-md font-medium transition-colors inline-flex items-center text-lg" data-id="element-603">
                Let's Work Together{' '}
                <ChevronRightIcon size={20} className="ml-2" data-id="element-604" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>;
};
export default HomePage;